import { useState, useEffect } from "react";
import EcommerceMetrics from "../../components/ecommerce/EcommerceMetrics";
import StatisticsChart from "../../components/ecommerce/StatisticsChart";
import Indices from "../../components/ecommerce/IndicesCard";
import PageMeta from "../../components/common/PageMeta";
import DateRangePicker from "../../components/daterange/DateRangePicker";
import api from "../../services/api";

// Interface para o tipo de dados da API
interface UtmRankItem {
  rank: number;
  value: string;
  count: number;
  revenue: number;
  percentage: string;
}

interface SalesData {
  summary: {
    totalSales: number;
    netRevenue: number;
    grossRevenue: number;
    paidOrders: number;
    averageTicket: number;
    averageRevenuePerDay: number;
  };
  conversion: {
    creditCard: { total: number; percentage: number };
    pix: { total: number; percentage: number };
    boleto: { total: number; percentage: number };
    refunded: { total: number; percentage: number };
    chargeback: { total: number; percentage: number };
  };
  utmStats?: {
    utm_source?: UtmRankItem[];
    utm_medium?: UtmRankItem[];
    utm_campaign?: UtmRankItem[];
    utm_content?: UtmRankItem[];
    utm_term?: UtmRankItem[];
  };
  revenueEvolution: {
    labels: string[];
    datasets: {
      netRevenue: number[];
      grossRevenue: number[];
    };
  };
  salesList: Array<any>;
}

export default function Home() {
  // Estados para os dados da API
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [salesData, setSalesData] = useState<SalesData | null>(null);

  // Carregar datas do localStorage (se existirem) ou definir padrão
  const getSavedDateRange = () => {
    try {
      const savedRange = localStorage.getItem('dateRangeSelection');
      if (savedRange) {
        const { startDate, endDate } = JSON.parse(savedRange);
        return {
          startDate: startDate ? new Date(startDate) : null,
          endDate: endDate ? new Date(endDate) : null
        };
      }
    } catch (error) {
      console.error("Erro ao recuperar datas do localStorage:", error);
    }

    // Se não houver dados salvos ou houver erro, definir hoje
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      startDate: today,
      endDate: today
    };
  };

  // Estado para armazenar as datas selecionadas
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(getSavedDateRange());

  // Função para buscar os dados de vendas
  const fetchSalesData = async (startDate?: Date | null, endDate?: Date | null) => {
    try {
      setIsLoading(true);

      // Preparar parâmetros de data se fornecidos
      let url = '/api/sales';
      const params = new URLSearchParams();

      if (startDate && endDate) {
        // Função auxiliar para formatar a data no fuso horário brasileiro
        const formatDateBR = (date: Date, endOfDay = false) => {
          // Cria uma cópia da data para não modificar a original
          const dateCopy = new Date(date);

          // Se for fim do dia, ajusta para 23:59:59
          if (endOfDay) {
            dateCopy.setHours(23, 59, 59, 999);
          } else {
            dateCopy.setHours(0, 0, 0, 0);
          }

          // Ajustar o fuso horário para GMT-3 (Brasil)
          // Usar o timezone de Brasília (GMT-3)
          return dateCopy.toLocaleString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).replace(/(\d+)\/(\d+)\/(\d+).*?(\d+):(\d+):(\d+)/, '$3-$2-$1T$4:$5:$6-03:00');
        };

        // Data inicial começa às 00:00:00 no fuso horário brasileiro
        params.append('startDate', formatDateBR(startDate));

        // Data final vai até 23:59:59 no fuso horário brasileiro
        params.append('endDate', formatDateBR(endDate, true));

        url = `${url}?${params.toString()}`;
      }

      // Chamada para a API
      const response = await api.get(url);

      if (response.data && response.data.status === 'success') {
        setSalesData(response.data.data);
      } else {
        console.error("Erro ao carregar dados de vendas:", response.data);
        // Exibir mensagem de erro
        alert("Erro ao carregar os dados de vendas");
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
      // Exibir mensagem de erro
      alert("Erro, por favor faça login novamente");
    } finally {
      setIsLoading(false);
    }
  };

  // Efeito para carregar os dados quando o componente é montado
  useEffect(() => {
    // Garantir que temos datas válidas para buscar
    if (dateRange.startDate && dateRange.endDate) {
      fetchSalesData(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange]); // Recarregar quando o intervalo de datas mudar

  // Função para quando o período de datas mudar
  const handleDateRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
    // Armazenar o novo intervalo de datas selecionado
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate
    });

    // Não precisamos chamar fetchSalesData aqui, pois o useEffect acima será acionado
    // quando dateRange mudar e fará a chamada para nós
    console.log("Intervalo de datas atualizado:", range);
  };

  return (
    <>
      <PageMeta
        title="Sellyo - Dashboard"
        description="Dashboard"
      />
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        <div className="col-span-12">
          <div className="flex justify-between items-center">
            <div className="w-auto">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Dashboard
              </h3>
            </div>
            <div className="w-auto">
              <DateRangePicker
                onChange={handleDateRangeChange}
                initialStartDate={dateRange.startDate}
                initialEndDate={dateRange.endDate}
              />
            </div>
          </div>
        </div>

        {/* EcommerceMetrics ocupa linha inteira */}
        <div className="col-span-12">
          <EcommerceMetrics
            summary={salesData?.summary}
            isLoading={isLoading}
          />
        </div>

        {/* UTM Comparison Chart e Gráfico ocupam 9 colunas */}
        <div className="col-span-12 space-y-6 xl:col-span-9">
          {/*<UtmComparisonChart
            utmRanking={salesData?.utmStats}
            isLoading={isLoading}
          />*/}
          <StatisticsChart
            salesData={salesData}
            isLoading={isLoading}
          />
        </div>

        {/* Índices ocupam 3 colunas */}
        <div className="col-span-12 xl:col-span-3">
          <Indices
            conversion={salesData?.conversion}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
}
